<template>
  <div>
    <LocaleTracker />
    <div
      class="fixed top-0 left-0 z-40 flex max-h-full w-full flex-col bg-surface/50 backdrop-blur dark-theme laptop:w-58"
      :class="{
        'h-screen': showMenuContent,
      }"
      @click.stop="showOnMobile = false"
    >
      <div class="flex flex-row items-center bg-surface">
        <button
          data-testid="sidebar-mobile-menu-toggle"
          class="flex p-4 text-main laptop:hidden"
          @click.stop="showOnMobile = !showOnMobile"
        >
          <SvgIcon :path="menuIconPath" :size="32" />
        </button>
        <SideBarLogo class="py-1 laptop:py-4" :available-locales="availableLocales" />
      </div>
      <div
        class="max-w-58 flex-grow overflow-x-hidden overflow-y-auto bg-surface px-2"
        :class="{ block: showMenuContent, hidden: !showMenuContent }"
        @click.stop
      >
        <SideBarAccountSwitcher v-if="me" data-testid="sidebar-switcher" :me="props.me" @toggle="(v) => (accountSwitcherOpen = v)" />
        <div v-if="accountSwitcherOpen" class="absolute top-0 left-0 h-full w-full bg-state-hover-neutral" />
        <!-- General items -->
        <SideBarItem data-testid="sidebar-dashboard" :to="{ name: 'dashboard' }">
          <span class="flex items-center">
            <LayoutGridIcon class="mr-2 lucide-icon-s" />
            {{ t('SideBar.dashboard') }}
            <span
              v-if="unreadNotificationsCount > 0 && !canSeeNotificationCenter"
              data-testid="sidebar-dashboard-unread-dot"
              class="ml-auto h-2 w-2 rounded-full bg-primary-main"
              role="status"
              :aria-label="t('SideBar.unreadNotifications')"
            />
          </span>
        </SideBarItem>

        <SideBarItem
          v-if="canSeeNotificationCenter"
          data-testid="sidebar-notifications"
          :to="{ name: 'notifications' }"
          @click="track('cta_click', { surface: 'nav', category: 'engagement', cta_position: 'sidebar', cta_id: 'notification_center' })"
        >
          <span class="flex items-center">
            <BellIcon class="mr-2 lucide-icon-s shrink-0" />
            <span class="grow truncate pr-2" :title="t('SideBar.notifications')">{{ t('SideBar.notifications') }}</span>
            <span
              v-if="unreadNotificationsCount > 0"
              data-testid="sidebar-notifications-unread-count"
              class="inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-primary-main px-1.5 txt-label-s-medium text-inverse"
              role="status"
            >
              {{ unreadNotificationsBadge }}
              <span class="sr-only">{{ t('SideBar.unreadNotifications') }}</span>
            </span>
          </span>
        </SideBarItem>

        <SideBarItem v-if="canManageUsers" data-testid="sidebar-user-management" :to="{ name: 'userList' }">
          <span class="flex items-center">
            <UsersIcon class="mr-2 lucide-icon-s" />
            {{ t('SideBar.userManagement') }}
          </span>
        </SideBarItem>

        <!-- Festival specific -->
        <p v-if="hasFestivalItems" class="my-3 px-5 text-body-s text-subtlest">{{ t('SideBar.festivalEvents.title') }}</p>

        <template v-if="me && sidebarItems.length && canSeeContentPages">
          <SideBarSideMenu
            data-testid="sidebar-content-pages"
            :label="t('SideBar.contentPages')"
            :to="sidebarItems[0].to"
            :side-bar-urls="getSidebarUrls(sidebarItems)"
            force-open
          >
            <template #prependLabel>
              <LibraryIcon class="mr-2 lucide-icon-s" />
            </template>
            <SideBarItem v-for="item in sidebarItems" :key="item.to" :to="item.to">{{ item.title }}</SideBarItem>
          </SideBarSideMenu>
        </template>

        <SideBarSideMenu
          v-if="canManageEvents || canSeeSpeakers"
          data-testid="sidebar-events"
          :label="`Events`"
          :to="getFirstVisibleTo(eventsSubMenuItems)"
          :side-bar-urls="getSidebarUrls(eventsSubMenuItems)"
          :exclude-routes="getExcludedRoutes(eventsSubMenuItems)"
          force-open
        >
          <template #prependLabel>
            <CalendarIcon class="mr-2 lucide-icon-s" />
          </template>
          <template v-for="item in eventsSubMenuItems" :key="item.label">
            <SideBarItem v-if="item.show" :to="item.to" :active-routes="item.activeRoutes">
              {{ item.label }}
            </SideBarItem>
          </template>
        </SideBarSideMenu>

        <SideBarItem v-if="canManageTickets" data-testid="sidebar-tickets" :to="{ name: 'tickets' }" :active-routes="TicketActiveRoutes">
          <span class="flex items-center">
            <TicketsIcon class="mr-2 lucide-icon-s" />
            {{ t('SideBar.tickets') }}
          </span>
        </SideBarItem>
        <SideBarItem v-if="canSeeCompany" data-testid="sidebar-partner-company" :to="{ name: 'partnerCompany' }">
          <span class="flex items-center">
            <Building2Icon class="mr-2 lucide-icon-s" />
            {{ t('SideBar.partnerCompany') }}
          </span>
        </SideBarItem>

        <SideBarItem v-if="canManageDocuments" data-testid="sidebar-documents" :to="{ name: 'documents' }">
          <span class="flex items-center">
            <FileTextIcon class="mr-2 lucide-icon-s" />
            {{ t('SideBar.booths') }}
          </span>
        </SideBarItem>

        <FestivalShopSubMenu v-if="canSeeFestivalShop" data-testid="sidebar-festival-shop" />

        <SideBarSideMenu
          v-if="
            canSeeLeads ||
            canSeeRecruitingPlus ||
            (canSeeProspectCatalog && isLeadGenEnabled) ||
            (canSeeVisitorProfiles && isNetworkingEnabled) ||
            (canSeeMeetings && isMeetingsEnabled)
          "
          data-testid="sidebar-leads"
          :label="t('SideBar.leads')"
          :to="getFirstVisibleTo(prospectSubMenuItems)"
          :side-bar-urls="getSidebarUrls(prospectSubMenuItems)"
          force-open
        >
          <template #prependLabel>
            <HandshakeIcon class="mr-2 lucide-icon-s" />
          </template>
          <template v-for="item in prospectSubMenuItems" :key="item.label">
            <SideBarItem
              v-if="item.show"
              :to="item.to"
              :active-routes="item.activeRoutes"
              :disable-router-active="item.disableRouterActive"
            >
              {{ item.label }}
            </SideBarItem>
          </template>
        </SideBarSideMenu>

        <ReviewsSideBarSubMenu v-if="canManageReviews" data-testid="sidebar-reviews-products" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiMenu, mdiClose } from '@mdi/js'
import { useDisplay } from '@ramp106/omrjs-core-ui'
import {
  BellIcon,
  LayoutGridIcon,
  UsersIcon,
  TicketsIcon,
  Building2Icon,
  LibraryIcon,
  CalendarIcon,
  HandshakeIcon,
  FileTextIcon,
} from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { type RouteLocationRaw, useRouter } from 'vue-router'

import ReviewsSideBarSubMenu from '@/components/SideBar/ReviewsSideBarSubMenu.vue'
import SideBarItem from '@/components/SideBar/SideBarItem.vue'
import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { useContentPages } from '@/composables/useContentPages'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { useNotificationUnreadCount } from '@/composables/useNotificationUnreadCount'
import type { MeQuery } from '@/gql/myomr'
import { $can } from '@/services/roles'
import { useMeStore } from '@/stores'

import SideBarLogo from './SideBarLogo.vue'
import LocaleTracker from '../LocaleTracker.vue'
import SvgIcon from '../SvgIcon.vue'
import FestivalShopSubMenu from './FestivalShop/FestivalShopSubMenu.vue'
import SideBarAccountSwitcher from './SideBarAccountSwitcher.vue'
import SideBarSideMenu from './SideBarSideMenu.vue'

const { t } = useI18n()
const { sidebarItems } = useContentPages()

const router = useRouter()

const meStore = useMeStore()
const { unreadCount: unreadNotificationsCount } = useNotificationUnreadCount()

const props = defineProps<{
  me: MeQuery['me'] | undefined
}>()

const availableLocales = [
  { text: 'Deutsch', value: 'de', flag: 'de' },
  { text: 'English', value: 'en', flag: 'gb' },
]
const { isMobile } = useDisplay()
const showOnMobile = ref(false)
const accountSwitcherOpen = ref(false)
const showMenuContent = computed(() => showOnMobile.value || !isMobile.value)
const menuIconPath = computed(() => (showOnMobile.value ? mdiClose : mdiMenu))

const disabledMenuItems: string[] = []
function checkRolePermission(permission: string) {
  return $can(permission, { for: 'User' }) && !disabledMenuItems.includes(permission)
}
const canManageDocuments = computed(() => props.me && checkRolePermission('viewDocuments'))
const canManageEvents = computed(() => props.me && hasChildEvents.value && checkRolePermission('viewEvents'))
const canManageReviews = computed(() => meStore.hasAcceptedReviewsTerms && checkRolePermission('manageReviews'))
const canManageTickets = computed(() => props.me && checkRolePermission('viewTickets'))
const canManageUsers = computed(() => checkRolePermission('manageUsers'))
const canSeeCompany = computed(() => checkRolePermission('viewCompany'))
const canSeeContentPages = computed(() => checkRolePermission('viewContentPages'))
const canSeeFestivalShop = computed(() => props.me && checkRolePermission('viewFestivalShop'))
const canSeeLeads = computed(() => checkRolePermission('viewLeads'))
const canSeeRecruitingPlus = computed(() => checkRolePermission('viewRecruitingPlus'))
const canSeeMeetings = computed(() => checkRolePermission('viewMeetings'))
const canSeeProspectCatalog = computed(() => checkRolePermission('viewProspectCatalog'))
const canSeeSpeakers = computed(() => checkRolePermission('viewSpeakers'))
const canSeeVisitorProfiles = computed(() => checkRolePermission('viewVisitorProfiles'))

const { enabled: isNotificationCenterEnabled } = useFeatureFlag(FEATURE_FLAG.NOTIFICATIONS.CENTER_RELEASE)
const canSeeNotificationCenter = computed(() => isNotificationCenterEnabled.value && checkRolePermission('manageReviews'))
const MAX_UNREAD_BADGE_COUNT = 9
const unreadNotificationsBadge = computed(() =>
  unreadNotificationsCount.value > MAX_UNREAD_BADGE_COUNT ? `${MAX_UNREAD_BADGE_COUNT}+` : `${unreadNotificationsCount.value}`,
)

const { enabled: isMeetingsEnabled } = useFeatureFlag(FEATURE_FLAG.FESTIVAL.IS_MEETINGS_ENABLED)
const { enabled: isLeadGenEnabled } = useFeatureFlag(FEATURE_FLAG.FESTIVAL.IS_LEAD_GEN_ENABLED)
const { enabled: isNetworkingEnabled } = useFeatureFlag(FEATURE_FLAG.FESTIVAL.IS_NETWORKING_ENABLED)

const EventActiveRoutes = ['eventEdit', 'EventAttendees', 'AttendeeUpload']
const TicketActiveRoutes = ['invitations', 'invitationQuota', 'invitationQuotaCsvUpload']

const hasChildEvents = computed(() => {
  return props.me?.partnerCompany?.hasChildEvents
})

export interface SidebarItemType {
  show?: boolean
  to: string | RouteLocationRaw
  label?: string
  activeRoutes?: string[]
  excludeRoutes?: string[]
  disableRouterActive?: boolean
}

function getSidebarUrls(items: Array<SidebarItemType>) {
  return items.map((item) => (typeof item.to === 'string' ? item.to : router.resolve(item.to).path))
}

function getExcludedRoutes(items: Array<SidebarItemType>): string[] {
  // Collect all excluded route names from submenu items
  return items.flatMap((item) => item.excludeRoutes || [])
}

const eventsSubMenuItems = computed(() => [
  {
    show: !!canManageEvents.value,
    to: { name: 'events' },
    activeRoutes: EventActiveRoutes,
    label: t('SideBar.events'),
    excludeRoutes: TicketActiveRoutes, // Exclude ticket routes from Events menu
  },
  {
    show: !!canSeeSpeakers.value,
    to: { name: 'speakerList' },
    label: t('SideBar.speakers'),
  },
])

function getFirstVisibleTo(items: Array<{ show?: boolean; to: string | RouteLocationRaw }>): RouteLocationRaw {
  const found = items.find((item) => !!item.show)
  return found ? found.to : { name: 'dashboard' }
}

const prospectSubMenuItems = computed(() => [
  {
    show: canSeeVisitorProfiles.value && isNetworkingEnabled.value,
    to: { name: 'visitorProfilesList' },
    activeRoutes: ['visitorProfilesList', 'visitorProfilesListTab'],
    label: t('SideBar.visitorProfiles'),
    disableRouterActive: true,
  },
  {
    show: canSeeProspectCatalog.value && isLeadGenEnabled.value,
    to: { name: 'prospectEventsIndex' },
    activeRoutes: [
      'prospectEventsIndex',
      'prospectEvents',
      'prospectEvent',
      'prospectsCatalog',
      'prospectLists',
      'prospectList',
      'deleteProspectList',
      'editProspectList',
      'sendProspectListTest',
      'sendProspectListInvitations',
    ],
    label: t('SideBar.prospectsCatalog'),
  },
  {
    show: canSeeLeads.value,
    to: { name: 'myleads' },
    activeRoutes: ['myleads', 'eventLeads'],
    label: t('SideBar.myleads'),
  },
  {
    show: canSeeRecruitingPlus.value,
    to: { name: 'recruitingPlus' },
    activeRoutes: ['recruitingPlus'],
    label: t('SideBar.hrReach'),
  },
  {
    show: canSeeMeetings.value && isMeetingsEnabled.value,
    to: { name: 'meetingsMyMeetings' },
    activeRoutes: ['meetings'],
    label: t('SideBar.meetings'),
  },
])

const hasFestivalItems = computed(() => {
  return (
    (props.me && sidebarItems.value.length && canSeeContentPages.value) ||
    canManageEvents.value ||
    canSeeSpeakers.value ||
    canManageTickets.value ||
    canSeeCompany.value ||
    canManageDocuments.value ||
    canSeeFestivalShop.value ||
    canSeeLeads.value ||
    canSeeRecruitingPlus.value ||
    (canSeeProspectCatalog.value && isLeadGenEnabled.value) ||
    (canSeeVisitorProfiles.value && isNetworkingEnabled.value) ||
    canManageReviews.value ||
    (canSeeMeetings.value && isMeetingsEnabled.value)
  )
})
</script>
